import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Alert, Box, Button } from '@mui/material';

import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import LoadingScreen from './components/UI/LoadingScreen';

// Dashboard and main pages.
//
// Everything except the login screen is lazy-loaded. A hotspot customer opening
// /wifi must not download the admin application: route-level code splitting keeps
// the initial bundle down to React + the shell + the portal itself, which is the
// difference between a portal that paints over a slow link and a white screen.
//
// The lazy components live in ./routes/lazyPages.js together with their `import()`
// loaders so routes/prefetch.js can warm the very same chunks from the sidebar.
import {
  Dashboard,
  Customers,
  PackagesAndParents,
  Billing,
  SubscriptionDetails,
  Payments,
  CreditLedger,
  Shop,
  Inventory,
  Support,
  Network,
  VlanManagement,
  Hotspot,
  RouterDetails,
  MonitoringDashboard,
  CustomerTraffic,
  Reports,
  Settings,
  RenewalLanding,
  PrintDocument,
  PrintIspDocument
} from './routes/lazyPages';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { token, loading, user, verificationPending, retryVerification, logout } = useAuth();
  if (loading) {
    return <LoadingScreen />;
  }

  if (token && !user && verificationPending) {
    return <Box sx={{ p: 3 }}><Alert severity="warning" action={<>
      <Button color="inherit" onClick={retryVerification}>Retry</Button>
      <Button color="inherit" onClick={logout}>Sign out</Button>
    </>}>Unable to verify your session. Check the connection and retry.</Alert></Box>;
  }
  return token ? children : <Navigate to="/login" replace />;
};

function App() {
  const { token, loading, user, verificationPending, retryVerification, logout } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (token && !user && verificationPending) {
    return <Box sx={{ p: 3 }}><Alert severity="warning" action={<>
      <Button color="inherit" onClick={retryVerification}>Retry</Button>
      <Button color="inherit" onClick={logout}>Sign out</Button>
    </>}>Unable to verify your session. Check the connection and retry.</Alert></Box>;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Outer boundary for routes rendered WITHOUT the shell (/login, /renew,
          /print, /traffic). Routes inside <Layout> are caught by the boundary in
          Layout.js, which clears only the content area instead of the whole screen
          (a full-screen overlay on every section change looked like a page reload). */}
      <React.Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login" element={token ? <Navigate to="/" replace /> : <Login />} />
        {/* STATIC EXPIRED SUBSCRIBERS: tokenised renewal page (services/api/renewals.js) */}
        <Route path="/renew/:token" element={<RenewalLanding />} />
        {/* HOTSPOT CUSTOMERS are NOT served by this app: /wifi is a server-rendered
            document (server/templates/wifiPortalPage.js) mounted ahead of the SPA, because
            a phone behind the walled garden cannot be asked to download a JS bundle
            (server/routes/wifiPortal.js). Kept deliberately separate from /renew — the two
            flows never share state. */}

        <Route path="/traffic" element={<CustomerTraffic />} />
        <Route path="/print/:docType/:id" element={<ProtectedRoute><PrintDocument /></ProtectedRoute>} />
        <Route path="/print/isp/:docType/:id" element={<ProtectedRoute><PrintIspDocument /></ProtectedRoute>} />
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="customers/*" element={<Customers />} />
          <Route path="packages-and-parents" element={<PackagesAndParents />} />
          <Route path="packages/*" element={<Navigate to="/packages-and-parents" replace />} />
          <Route path="billing/*" element={<Billing />} />
          <Route path="billing/subscriptions/:subscriptionId" element={<SubscriptionDetails />} />
          <Route path="payments/*" element={<Payments />} />
          <Route path="credit-ledger" element={<CreditLedger />} />
          <Route path="support/*" element={<Support />} />
          <Route path="network/*" element={<Network />} />
          <Route path="network/vlans" element={<VlanManagement />} />
          <Route path="network/router/:routerId" element={<RouterDetails />} />
          <Route path="network/hotspot" element={<Hotspot />} />
          <Route path="network/hotspot/*" element={<Hotspot />} />
          <Route path="network/hotspot-server" element={<Navigate to="/network/hotspot" replace />} />
          <Route path="network/hotspot-users" element={<Navigate to="/network/hotspot" replace />} />
          {/* legacy hotspot-server/hotspot-users URLs redirect to /network/hotspot above */}
<Route path="network/monitoring" element={<MonitoringDashboard />} />
          <Route path="reports/*" element={<Reports />} />
          <Route path="settings/*" element={<Settings />} />
          <Route path="shop" element={<Shop />} />
          {/* Nested module/section routes: /shop/sales/invoice, /shop/hr/payroll, …
              These are the canonical URLs Shop() navigates to; the legacy
              ?module=&section= form is still accepted for backward compatibility. */}
          <Route path="shop/:moduleKey" element={<Shop />} />
          <Route path="shop/:moduleKey/:sectionKey" element={<Shop />} />
          <Route path="inventory/*" element={<Inventory />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      </React.Suspense>
    </Box>
  );
}

export default App;
